import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import { Loader2, Shield } from 'lucide-react';

const Login: React.FC = () => {
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginIdentifier, password);
      navigate('/');
    } catch (error) {
      // обработано в AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn(
      'relative min-h-screen flex items-center justify-center p-4 overflow-hidden',
      'bg-[#F5F3EF] dark:bg-[#0F0F17]'
    )}>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, #F5DC8A, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(circle, #C9A227, transparent 70%)' }}
      />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-[#F5DC8A] via-[#C9A227] to-[#9C7E1B] flex items-center justify-center shadow-xl shadow-[#C9A227]/30 mb-4">
            <Shield className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111111] dark:text-white">
            Админка
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Войдите для управления платформой
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1A22] rounded-3xl p-8 shadow-xl shadow-black/5 border border-[#EFEAE0] dark:border-[#2D2D38]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="loginIdentifier"
                className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400"
              >
                Email или логин
              </Label>
              <Input
                id="loginIdentifier"
                type="text"
                placeholder="admin@example.com"
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="username"
                className="h-12 rounded-2xl bg-[#F5F3EF] dark:bg-[#0F0F17] border-0 text-base placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#C9A227]/40 focus-visible:ring-offset-0"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400"
              >
                Пароль
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Введите пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="current-password"
                className="h-12 rounded-2xl bg-[#F5F3EF] dark:bg-[#0F0F17] border-0 text-base placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#C9A227]/40 focus-visible:ring-offset-0"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 rounded-2xl bg-[#111111] hover:bg-[#1A1A22] text-white text-base font-semibold tracking-wide shadow-lg shadow-black/10 transition-all"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Вход...
                </>
              ) : (
                'Войти'
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-6">
          Защищённая зона • только для авторизованных
        </p>
      </div>
    </div>
  );
};

export default Login;
